import { useMutation } from '@tanstack/react-query';
import {
  type RemoveIngredientMutation,
  type PairingMutation,
  MutationTypes,
} from '@hungryroot/cart';
import { type CartPairing, type CartProduct } from '@hungryroot/rest';
import {
  useCustomization,
  type CustomizationState,
} from '../../../CustomizationProvider/CustomizationProvider';

interface UpdateBundleParams {
  bundleId: number;
  pairing: CartPairing;
  modifications: PairingMutation[];
}

interface CreateBundleParams {
  modifications: PairingMutation[];
  pairingIngredients: CartProduct[];
  pairing: CartPairing;
}

export interface UseEditRecipeMutations {
  updateBundle(params: UpdateBundleParams): Promise<void>;
  createBundle(params: CreateBundleParams): Promise<void>;
  loading: boolean;
}

// Implementing this as a mutation so that
// HTTP requests still go through even after component is unmounted
function useUpdateBundleMutation(customization: CustomizationState) {
  return useMutation({
    mutationFn: async (params: UpdateBundleParams) => {
      if (!('mutations' in customization)) {
        return;
      }
      const {
        mutations: { swapRecipe, addProductsToBundle, removeProductFromBundle },
      } = customization;

      const { pairing, modifications, bundleId } = params;
      for (const modification of modifications) {
        switch (modification.type) {
          case MutationTypes.RemoveIngredient: {
            const { reasonId, ingredientId } = modification;
            await removeProductFromBundle({
              bundleId,
              productId: ingredientId,
              reasonId,
            });
            break;
          }
          case MutationTypes.AddIngredient: {
            const { ingredientId } = modification;
            await addProductsToBundle({
              bundleId,
              productIds: [ingredientId],
            });
            break;
          }
          case MutationTypes.Swap: {
            await swapRecipe({
              bundleId,
              recipe: pairing,
            });
            break;
          }
        }
      }
    },
  });
}

function useCreateBundleMutation(customization: CustomizationState) {
  return useMutation({
    mutationFn: async function createBundle(params: CreateBundleParams) {
      if (!('mutations' in customization)) {
        return;
      }

      const {
        mutations: { addCartPairing },
      } = customization;
      const { modifications, pairingIngredients, pairing } = params;
      const initialIngredientIds = pairingIngredients.map(({ id }) => id);
      const removals: Record<string, RemoveIngredientMutation> = {};

      modifications.forEach((modificiation) => {
        switch (modificiation.type) {
          case MutationTypes.AddIngredient: {
            delete removals[modificiation.ingredientId];
            break;
          }
          case MutationTypes.RemoveIngredient: {
            removals[modificiation.ingredientId] = modificiation;
            break;
          }
        }
      }, initialIngredientIds);

      const removalsArray = Object.keys(removals).map((key) => removals[key]);

      addCartPairing({
        modifications: removalsArray.length === 0 ? undefined : removalsArray,
        pairing,
        ingredients: pairingIngredients.filter(({ id }) => {
          return removals[id] === undefined;
        }),
      });
    },
  });
}

// This is only compatible with bundilized orders
export function useEditRecipeMutations(): UseEditRecipeMutations {
  const customization = useCustomization();
  const updateBundleMutation = useUpdateBundleMutation(customization);
  const createBundleMutation = useCreateBundleMutation(customization);

  return {
    loading:
      updateBundleMutation.isPending === true ||
      createBundleMutation.isPending === true,
    async updateBundle(params) {
      await updateBundleMutation.mutateAsync(params);
    },
    async createBundle(params) {
      await createBundleMutation.mutateAsync(params);
    },
  };
}
